import React, { useEffect, useState } from "react";
import { currency } from "../App";
import { adminGet, adminPost, getPaymentSettings, updatePaymentSetting } from "../common/api/api";

const PAYMENT_STYLES = {
  pending: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30",
  paid:    "text-green-400 bg-green-400/10 border-green-400/30",
  failed:  "text-red-400 bg-red-400/10 border-red-400/30",
  refunded:"text-purple-400 bg-purple-400/10 border-purple-400/30",
};

const PAYMENT_SETTINGS_KEY = "checkout_payment_settings";

const DEFAULT_PAYMENT_SETTINGS = {
  title: "Secure Checkout",
  subtitle: "Fast delivery with secure payment options",
  note: "Online payment methods are captured as selected payment preference in this demo flow. Final gateway processing can be integrated with your payment provider.",
  methods: [
    { value: "cod", label: "Cash on Delivery", hint: "Pay when your order arrives", icon: "🚚", kind: "cod", enabled: true, accountName: "", accountNumber: "", accountLink: "", qrCodeUrl: "" },
    { value: "credit_card", label: "Credit Card", hint: "Visa / Amex / Others", icon: "💳", kind: "card", enabled: true, accountName: "TechZone Payments", accountNumber: "", accountLink: "", qrCodeUrl: "" },
    { value: "debit_card", label: "Debit Card", hint: "Any bank debit card", icon: "🏦", kind: "card", enabled: true, accountName: "TechZone Payments", accountNumber: "", accountLink: "", qrCodeUrl: "" },
    { value: "master_card", label: "Master Card", hint: "Mastercard secure payment", icon: "🛡", kind: "card", enabled: true, accountName: "TechZone Payments", accountNumber: "", accountLink: "", qrCodeUrl: "" },
    { value: "easypaisa", label: "Easypaisa", hint: "Pay with Easypaisa wallet", icon: "📱", kind: "wallet", enabled: true, accountName: "TechZone Easypaisa", accountNumber: "", accountLink: "", qrCodeUrl: "" },
    { value: "jazzcash", label: "JazzCash", hint: "Pay with JazzCash wallet", icon: "⚡", kind: "wallet", enabled: true, accountName: "TechZone JazzCash", accountNumber: "", accountLink: "", qrCodeUrl: "" },
  ],
};

const cloneDefaults = () => ({
  ...DEFAULT_PAYMENT_SETTINGS,
  methods: DEFAULT_PAYMENT_SETTINGS.methods.map((method) => ({ ...method })),
});

const AdminPayments = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [checkoutSettings, setCheckoutSettings] = useState(cloneDefaults());
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [newMethod, setNewMethod] = useState({ value: "", label: "", hint: "", icon: "💠", kind: "wallet", accountName: "", accountNumber: "", accountLink: "", qrCodeUrl: "" });

  const fetchOrders = async () => {
    setLoading(true);
    const data = await adminGet("/allorders");
    setOrders(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  useEffect(() => { fetchOrders(); }, []);

  const loadSettings = async () => {
    setSettingsLoading(true);
    try {
      const data = await getPaymentSettings();
      const raw = data?.[PAYMENT_SETTINGS_KEY];
      if (raw) {
        const parsed = JSON.parse(raw);
        setCheckoutSettings({
          ...cloneDefaults(),
          ...parsed,
          methods: Array.isArray(parsed?.methods) ? parsed.methods : cloneDefaults().methods,
        });
      }
    } catch {
      setCheckoutSettings(cloneDefaults());
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => { loadSettings(); }, []);

  const updatePaymentStatus = async (id, paymentStatus) => {
    await adminPost("/updatepaymentstatus", { id, paymentStatus });
    fetchOrders();
  };

  const saveCheckoutSettings = async () => {
    setSettingsSaving(true);
    await updatePaymentSetting(PAYMENT_SETTINGS_KEY, JSON.stringify(checkoutSettings));
    setSettingsSaving(false);
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 2500);
  };

  const addMethod = async () => {
    if (!newMethod.value.trim() || !newMethod.label.trim()) return alert("Method value and label are required");
    setCheckoutSettings((prev) => ({
      ...prev,
      methods: [...prev.methods, { ...newMethod, value: newMethod.value.trim(), label: newMethod.label.trim(), hint: newMethod.hint.trim(), enabled: true }],
    }));
    setNewMethod({ value: "", label: "", hint: "", icon: "💠", kind: "wallet", accountName: "", accountNumber: "", accountLink: "", qrCodeUrl: "" });
  };

  const removeMethod = (value) => {
    setCheckoutSettings((prev) => ({
      ...prev,
      methods: prev.methods.filter((method) => method.value !== value),
    }));
  };

  const toggleMethod = (value) => {
    setCheckoutSettings((prev) => ({
      ...prev,
      methods: prev.methods.map((method) => method.value === value ? { ...method, enabled: !method.enabled } : method),
    }));
  };

  const filtered = filter === "all" ? orders : orders.filter((o) => o.paymentStatus === filter);

  const totalRevenue = orders.filter(o => o.paymentStatus === "paid").reduce((sum, o) => sum + (o.totalAmount || 0), 0);
  const pendingAmount = orders.filter(o => o.paymentStatus === "pending").reduce((sum, o) => sum + (o.totalAmount || 0), 0);

  const counts = { pending: 0, paid: 0, failed: 0, refunded: 0 };
  orders.forEach((o) => { if (counts[o.paymentStatus] !== undefined) counts[o.paymentStatus]++; });

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Payments</h1>
        <p className="text-sm text-[#4a5568] mt-0.5">Track payment status for all orders</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="p-4 bg-green-400/[0.08] border border-green-400/20 rounded-xl">
          <p className="text-xs text-[#4a5568] mb-1">Total Revenue</p>
          <p className="text-2xl font-bold text-green-400">{currency}{totalRevenue.toFixed(2)}</p>
          <p className="text-xs text-[#4a5568] mt-1">{counts.paid} paid orders</p>
        </div>
        <div className="p-4 bg-yellow-400/[0.08] border border-yellow-400/20 rounded-xl">
          <p className="text-xs text-[#4a5568] mb-1">Pending Amount</p>
          <p className="text-2xl font-bold text-yellow-400">{currency}{pendingAmount.toFixed(2)}</p>
          <p className="text-xs text-[#4a5568] mt-1">{counts.pending} pending</p>
        </div>
        <div className="p-4 bg-red-400/[0.08] border border-red-400/20 rounded-xl">
          <p className="text-xs text-[#4a5568] mb-1">Failed</p>
          <p className="text-2xl font-bold text-red-400">{counts.failed}</p>
          <p className="text-xs text-[#4a5568] mt-1">failed payments</p>
        </div>
        <div className="p-4 bg-purple-400/[0.08] border border-purple-400/20 rounded-xl">
          <p className="text-xs text-[#4a5568] mb-1">Refunded</p>
          <p className="text-2xl font-bold text-purple-400">{counts.refunded}</p>
          <p className="text-xs text-[#4a5568] mt-1">refunded orders</p>
        </div>
      </div>

      {/* Checkout Popup / Payment Methods Editor */}
      <div className="mb-6 rounded-2xl border border-white/[0.08] bg-[#0a0f1e] p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-bold text-white">Checkout Popup Settings</h2>
            <p className="text-sm text-[#4a5568] mt-0.5">Edit checkout popup text and control which payment methods appear to customers.</p>
          </div>
          <button
            onClick={saveCheckoutSettings}
            disabled={settingsSaving}
            className="px-4 py-2 rounded-lg bg-[rgba(0,212,255,0.15)] text-[#00d4ff] border border-[rgba(0,212,255,0.3)] text-sm hover:bg-[rgba(0,212,255,0.22)] disabled:opacity-50"
          >
            {settingsSaving ? "Saving..." : "Save Checkout Settings"}
          </button>
        </div>

        {settingsLoading ? (
          <div className="text-sm text-[#4a5568] py-4">Loading settings...</div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_1fr] gap-5">
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[#a0aec0] mb-1 block">Popup Title</label>
                <input
                  value={checkoutSettings.title}
                  onChange={(e) => setCheckoutSettings((prev) => ({ ...prev, title: e.target.value }))}
                  className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] text-sm focus:outline-none focus:border-[#00d4ff]/60"
                />
              </div>
              <div>
                <label className="text-xs text-[#a0aec0] mb-1 block">Popup Subtitle</label>
                <input
                  value={checkoutSettings.subtitle}
                  onChange={(e) => setCheckoutSettings((prev) => ({ ...prev, subtitle: e.target.value }))}
                  className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] text-sm focus:outline-none focus:border-[#00d4ff]/60"
                />
              </div>
              <div>
                <label className="text-xs text-[#a0aec0] mb-1 block">Note / Footer Text</label>
                <textarea
                  value={checkoutSettings.note}
                  onChange={(e) => setCheckoutSettings((prev) => ({ ...prev, note: e.target.value }))}
                  rows={4}
                  className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] text-sm focus:outline-none focus:border-[#00d4ff]/60 resize-y"
                />
              </div>
              {settingsSaved && <p className="text-xs text-green-400">✓ Settings saved</p>}
            </div>

            <div className="space-y-3">
              <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-4">
                <h3 className="text-white font-semibold mb-3">Current Payment Methods</h3>
                <div className="space-y-2 max-h-[310px] overflow-y-auto pr-1">
                  {checkoutSettings.methods.map((method) => (
                    <div key={method.value} className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3 space-y-3">
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() => toggleMethod(method.value)}
                          className={`mt-0.5 px-2 py-1 rounded-full text-[10px] border ${method.enabled ? "border-green-400/30 text-green-400 bg-green-400/10" : "border-white/[0.1] text-[#4a5568] bg-white/[0.03]"}`}
                        >
                          {method.enabled ? "On" : "Off"}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white font-semibold">{method.icon} {method.label}</p>
                          <p className="text-xs text-[#4a5568] truncate">{method.value} · {method.kind} · {method.hint}</p>
                        </div>
                        <button
                          onClick={() => removeMethod(method.value)}
                          className="text-xs px-2.5 py-1 rounded-lg border border-red-400/20 text-red-400 hover:bg-red-400/10 shrink-0"
                        >
                          Remove
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input
                          value={method.accountName || ""}
                          onChange={(e) => setCheckoutSettings((prev) => ({
                            ...prev,
                            methods: prev.methods.map((item) => item.value === method.value ? { ...item, accountName: e.target.value } : item),
                          }))}
                          placeholder="Account / Payee name"
                          className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] text-xs focus:outline-none focus:border-[#00d4ff]/60"
                        />
                        <input
                          value={method.accountNumber || ""}
                          onChange={(e) => setCheckoutSettings((prev) => ({
                            ...prev,
                            methods: prev.methods.map((item) => item.value === method.value ? { ...item, accountNumber: e.target.value } : item),
                          }))}
                          placeholder="Account / wallet number"
                          className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] text-xs focus:outline-none focus:border-[#00d4ff]/60"
                        />
                        <input
                          value={method.accountLink || ""}
                          onChange={(e) => setCheckoutSettings((prev) => ({
                            ...prev,
                            methods: prev.methods.map((item) => item.value === method.value ? { ...item, accountLink: e.target.value } : item),
                          }))}
                          placeholder="Payment link / IBAN / URL"
                          className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] text-xs focus:outline-none focus:border-[#00d4ff]/60 sm:col-span-2"
                        />
                        <input
                          value={method.qrCodeUrl || ""}
                          onChange={(e) => setCheckoutSettings((prev) => ({
                            ...prev,
                            methods: prev.methods.map((item) => item.value === method.value ? { ...item, qrCodeUrl: e.target.value } : item),
                          }))}
                          placeholder="QR image URL (optional)"
                          className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] text-xs focus:outline-none focus:border-[#00d4ff]/60 sm:col-span-2"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-dashed border-white/[0.1] bg-white/[0.02] p-4">
                <h3 className="text-white font-semibold mb-3">Add Payment Method</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    value={newMethod.label}
                    onChange={(e) => setNewMethod((prev) => ({ ...prev, label: e.target.value }))}
                    placeholder="Label"
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] text-sm focus:outline-none focus:border-[#00d4ff]/60"
                  />
                  <input
                    value={newMethod.value}
                    onChange={(e) => setNewMethod((prev) => ({ ...prev, value: e.target.value }))}
                    placeholder="value"
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] text-sm focus:outline-none focus:border-[#00d4ff]/60"
                  />
                  <input
                    value={newMethod.icon}
                    onChange={(e) => setNewMethod((prev) => ({ ...prev, icon: e.target.value }))}
                    placeholder="Icon"
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] text-sm focus:outline-none focus:border-[#00d4ff]/60"
                  />
                  <input
                    value={newMethod.accountName}
                    onChange={(e) => setNewMethod((prev) => ({ ...prev, accountName: e.target.value }))}
                    placeholder="Account / payee name"
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] text-sm focus:outline-none focus:border-[#00d4ff]/60 sm:col-span-2"
                  />
                  <input
                    value={newMethod.accountNumber}
                    onChange={(e) => setNewMethod((prev) => ({ ...prev, accountNumber: e.target.value }))}
                    placeholder="Account / wallet number"
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] text-sm focus:outline-none focus:border-[#00d4ff]/60 sm:col-span-2"
                  />
                  <input
                    value={newMethod.accountLink}
                    onChange={(e) => setNewMethod((prev) => ({ ...prev, accountLink: e.target.value }))}
                    placeholder="Payment link / IBAN / URL"
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] text-sm focus:outline-none focus:border-[#00d4ff]/60 sm:col-span-2"
                  />
                  <input
                    value={newMethod.qrCodeUrl}
                    onChange={(e) => setNewMethod((prev) => ({ ...prev, qrCodeUrl: e.target.value }))}
                    placeholder="QR image URL"
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] text-sm focus:outline-none focus:border-[#00d4ff]/60 sm:col-span-2"
                  />
                  <select
                    value={newMethod.kind}
                    onChange={(e) => setNewMethod((prev) => ({ ...prev, kind: e.target.value }))}
                    className="w-full bg-[#0d1527] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] text-sm focus:outline-none focus:border-[#00d4ff]/60"
                  >
                    <option value="cod">COD</option>
                    <option value="card">Card</option>
                    <option value="wallet">Wallet</option>
                    <option value="other">Other</option>
                  </select>
                  <input
                    value={newMethod.hint}
                    onChange={(e) => setNewMethod((prev) => ({ ...prev, hint: e.target.value }))}
                    placeholder="Hint text"
                    className="sm:col-span-2 w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] text-sm focus:outline-none focus:border-[#00d4ff]/60"
                  />
                </div>
                <button
                  onClick={addMethod}
                  className="mt-3 px-4 py-2 rounded-lg bg-[rgba(0,212,255,0.15)] text-[#00d4ff] border border-[rgba(0,212,255,0.3)] text-sm hover:bg-[rgba(0,212,255,0.22)]"
                >
                  + Add Method
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {["all", "pending", "paid", "failed", "refunded"].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-all cursor-pointer capitalize ${
              filter === s ? "bg-[rgba(0,212,255,0.15)] text-[#00d4ff] border-[rgba(0,212,255,0.4)]" : "text-[#4a5568] border-white/[0.08] hover:border-white/[0.2] hover:text-[#a0aec0]"
            }`}>
            {s}
          </button>
        ))}
      </div>

      {/* Scrollable table wrapper */}
      <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
        <div className="min-w-[680px]">
      {/* Table Header */}
      <div className="grid grid-cols-[1fr_1fr_100px_120px_140px_120px] gap-3 px-4 py-3 bg-white/[0.05] rounded-t-xl text-[#4a5568] text-xs font-bold uppercase tracking-wider">
        <span>Order ID</span>
        <span>Customer</span>
        <span>Amount</span>
        <span>Method</span>
        <span>Payment Status</span>
        <span>Update</span>
      </div>

      <div className="rounded-b-xl overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-[#4a5568]">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-[#4a5568]">No payments found.</div>
        ) : (
          filtered.map((order) => (
            <div key={order._id} className="grid grid-cols-[1fr_1fr_100px_120px_140px_120px] gap-3 items-center px-4 py-3.5 border-b border-white/[0.05] hover:bg-white/[0.02] last:border-b-0 transition-colors">
              <span className="text-[#a0aec0] font-mono text-xs">#{order._id?.slice(-8).toUpperCase()}</span>
              <div className="min-w-0">
                <p className="text-[#e2e8f0] text-sm truncate">{order.userName}</p>
                <p className="text-[#4a5568] text-xs truncate">{order.userEmail}</p>
              </div>
              <span className="text-[#00d4ff] font-bold text-sm">{currency}{order.totalAmount || 0}</span>
              <span className="text-[#a0aec0] text-xs capitalize">{order.paymentMethod || "cod"}</span>
              <span className={`text-xs px-2.5 py-1 rounded-full border w-fit ${PAYMENT_STYLES[order.paymentStatus] || PAYMENT_STYLES.pending}`}>
                {order.paymentStatus || "pending"}
              </span>
              <select
                value={order.paymentStatus || "pending"}
                onChange={(e) => updatePaymentStatus(order._id, e.target.value)}
                className="bg-white/[0.05] border border-white/[0.1] rounded-lg px-2 py-1.5 text-[#e2e8f0] text-xs focus:outline-none focus:border-[#00d4ff]/60 cursor-pointer"
                style={{ background: '#0d1527' }}
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
          ))
        )}
      </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPayments;
